import { FE_VERSION } from '@/constants.ts';
import router from '@/router/router';
import {
    browserProfilingIntegration,
    browserTracingIntegration,
    captureConsoleIntegration,
    extraErrorDataIntegration,
    httpClientIntegration,
    init,
    replayIntegration,
    reportingObserverIntegration,
    startSpan,
} from '@sentry/vue';
import type { App } from 'vue';

import { getEnv, getEnvDescription } from '../utils/envUtils';

const ignoredSpans = ['resource', 'measure'];

const env = getEnv();
const dsn = env.VITE_SENTRY_DSN;

export const initSentry = (app: App) => {
    if (dsn?.length) {
        init({
            app,
            dsn,
            debug: false,
            release: FE_VERSION,
            enabled: env.TEST || env.PROD,
            environment: getEnvDescription(),
            // beforeSendSpan does not work for transaction based spans
            beforeSendTransaction: transaction => {
                if (transaction.spans) {
                    transaction.spans = transaction.spans.filter(span =>
                        ignoredSpans.every(ignored => !(span.op ?? '').includes(ignored))
                    );
                }
                return transaction;
            },
            // if we have to ignore some errors in the future because they are "expected"
            ignoreErrors: [],
            // tweak these if we have to much traffic, reduces samples by randomly dropping them
            tracesSampleRate: 1,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1,
            profilesSampleRate: 0,
            integrations: [
                browserProfilingIntegration(),
                browserTracingIntegration({ router }),
                captureConsoleIntegration({
                    // for some very weird reason the sentry level "warning" is only named warn here.
                    levels: ['error', 'warn'],
                }),
                extraErrorDataIntegration(),
                httpClientIntegration({
                    failedRequestStatusCodes: [
                        [400, 499], // NoSonar
                        [500, 599], // NoSonar
                    ],
                }),
                replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                    networkDetailAllowUrls: [
                        'https://gutschrifterstellung-web-api.public.aks.dev.bundo.eu/api/v1/',
                        'https://gutschrifterstellung-web-api.public.aks.test.bundo.eu/api/v1/',
                        'https://gutschrifterstellung-web-api.public.aks.prod.bundo.eu/api/v1/',
                    ],
                    networkRequestHeaders: ['X-Custom-Header'],
                    networkResponseHeaders: ['X-Custom-Header'],
                }),
                reportingObserverIntegration(),
            ],
        });
    }
};

export const track = (event: string, data?: Record<string, unknown>) => {
    startSpan({ op: 'custom', name: event }, span => {
        Object.entries(data ?? {}).forEach(([key, value]) => {
            span.setAttribute(key, JSON.stringify(value));
        });
    });
};
